import * as React from 'react';
import type { GestureResponderEvent, GestureResponderHandlers } from 'react-native';

export type GestureHandlers = {
    /**
     * Events when user is hovering.
     */
    onHoverIn?: () => void;
    onHoverOut?: () => void;

    onFocus?: (event: GestureResponderEvent) => void;
    onBlur?: (event: GestureResponderEvent) => void;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onMouseLeave?: (event: React.MouseEvent) => void;
    onPressIn?: (event: GestureResponderEvent) => void;
    onPressOut?: (event: GestureResponderEvent) => void;
} & GestureResponderHandlers;

let isHoverEnabled = false;

const canUseDOM = !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
);

if (canUseDOM) {
    /*
     * Web browsers emulate mouse events (and hover states) after touch events.
     * This code infers when the currently-in-use modality supports hover
     * (including for multi-modality devices) and considers "hover" to be enabled
     * if a mouse movement occurs more than 1 second after the last touch event.
     * This threshold is long enough to account for longer delays between the
     * browser firing touch and mouse events on low-powered devices.
     */
    const HOVER_THRESHOLD_MS = 1000;
    let lastTouchTimestamp = 0;

    function enableHover() {
        if (isHoverEnabled || Date.now() - lastTouchTimestamp < HOVER_THRESHOLD_MS) {
            return;
        }
        isHoverEnabled = true;
    }

    function disableHover() {
        lastTouchTimestamp = Date.now();
        if (isHoverEnabled) {
            isHoverEnabled = false;
        }
    }

    document.addEventListener('touchstart', disableHover, true);
    document.addEventListener('touchmove', disableHover, true);
    document.addEventListener('mousemove', enableHover, true);
}

/**
 * Position in the element.
 */
export interface InteractiveHoverZone {
    // upper half
    top: boolean;
    // right half
    right: boolean;
    // lower half
    bottom: boolean;
    // left half
    left: boolean;
    // true if cursor is in the center of the component
    // | 25% | center (50%) | 25% |
    center: boolean;
}

/**
 * State of interactivity between an user and a view.
 */
export interface InteractiveState {
    /**
     * When the element is being dragged.
     */
    dragging: boolean;
    /**
     * When the mouse is hover the element.
     */
    hovered: boolean;
    /**
     * When the item is focused with keyboard navigation.
     */
    focused: boolean;
    /**
     * When the item is focused by keyboard or mouse.
     */
    active: boolean;
    /**
     * When the user is pressing the element
     */
    pressed: boolean;
    /**
     * Events to pass to the underlying view (DOM /CSS mode).
     */
    domEvents: {
        onMouseEnter: (event: React.MouseEvent) => void;
        onMouseLeave: (event: React.MouseEvent) => void;
        onMouseDown: (event: React.MouseEvent) => void;
        onMouseUp: (event: React.MouseEvent) => void;
        onMouseMove?: (event: React.MouseEvent) => void;
        onFocus: (event: React.FocusEvent) => void;
        onBlur: (event: React.FocusEvent) => void;
    };
    /**
     * Events to pass to the underlying view (react-native).
     */
    events: Record<string, unknown>;
    /**
     * Zone being hovered.
     */
    hoverZone: InteractiveHoverZone;
}

const defaultHoverZone: InteractiveHoverZone = {
    top: false,
    right: false,
    bottom: false,
    left: false,
    center: false,
};

export type InteractiveOptions = {
    /**
     * Delay before considering that the user is hovering the element (in and out).
     */
    hoverDelay?: number;
    /**
     * Delay before considering that the user is hovering the element.
     */
    hoverDelayIn?: number;
    /**
     * Delay before considering that the user is stopping hovering the element.
     */
    hoverDelayOut?: number;
    /**
     * Should we detect the position in the element ?
     */
    detectHoverZone?: boolean;
} & GestureHandlers;

/**
 * Hook to use create an interactive component.
 */
export function useInteractiveState(props: InteractiveOptions = {}): InteractiveState {
    const { hoverDelay = 0, detectHoverZone = false, onHoverIn, onHoverOut, ...rest } = props;
    const { hoverDelayIn = hoverDelay, hoverDelayOut = hoverDelay, ...gestureHandlers } = rest;

    const [hoverZone, setHoverZone] = React.useState<InteractiveHoverZone>(defaultHoverZone);
    const [focused, setFocused] = React.useState(false);
    const [pressed, setPressed] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);
    const [dragging, setDragging] = React.useState(false);
    const hoveredRef = React.useRef(hovered);
    const pressedElementRef = React.useRef<HTMLElement | null>(null);
    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const hoverZoneTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const clearHoverTimeout = () => {
        if (!hoverTimeoutRef.current) {
            return;
        }

        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
    };

    //
    // Update the hoverZone from the position of the mouse
    //
    const computeHoverZone = (currentTarget: Element, clientX: number, clientY: number) => {
        const rect = currentTarget.getBoundingClientRect();
        const halfHeight = Math.round(rect.height / 2);
        const halfWidth = Math.round(rect.width / 2);
        const quarterWidth = Math.round(rect.width / 4);

        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const newHoverZone: InteractiveHoverZone = {
            center: x > quarterWidth * 1 && x < quarterWidth * 3,
            top: y < halfHeight,
            right: x > halfWidth,
            bottom: y > halfHeight,
            left: x < halfWidth,
        };

        if (
            newHoverZone.center === hoverZone.center &&
            newHoverZone.top === hoverZone.top &&
            newHoverZone.right === hoverZone.right &&
            newHoverZone.left === hoverZone.left &&
            newHoverZone.bottom === hoverZone.bottom
        ) {
            return;
        }

        setHoverZone(newHoverZone);
    };

    const handleGrant = () => {
        setDragging(true);
    };

    const handleRelease = () => {
        setDragging(false);
    };

    //
    // Mouse events
    //
    const onMouseLeave = () => {
        clearHoverTimeout();

        if (!hoveredRef.current) {
            return;
        }

        hoveredRef.current = false;

        const dispatchHoverOut = () => {
            setHovered(false);
            if (onHoverOut) {
                onHoverOut();
            }
        };

        if (hoverDelayOut > 0) {
            clearHoverTimeout();
            hoverTimeoutRef.current = setTimeout(dispatchHoverOut, hoverDelayOut);
        } else {
            dispatchHoverOut();
        }
    };

    const onMouseEnter = (event: React.MouseEvent) => {
        clearHoverTimeout();

        if (!isHoverEnabled || hoveredRef.current) {
            return;
        }

        const { currentTarget, clientX, clientY } = event;
        hoveredRef.current = true;

        const dispatchHover = () => {
            setHovered(true);
            if (onHoverIn) {
                onHoverIn();
            }

            if (detectHoverZone) {
                computeHoverZone(currentTarget, clientX, clientY);
            }
        };

        if (hoverDelayIn > 0) {
            hoverTimeoutRef.current = setTimeout(dispatchHover, hoverDelayIn);
        } else {
            dispatchHover();
        }
    };

    const onMouseMove =
        detectHoverZone || hoverZoneTimeoutRef.current
            ? (event: React.MouseEvent) => {
                  const { currentTarget, clientX, clientY } = event;

                  if (hoverZoneTimeoutRef.current) {
                      clearTimeout(hoverZoneTimeoutRef.current);
                  }

                  if (detectHoverZone) {
                      hoverZoneTimeoutRef.current = setTimeout(() => {
                          computeHoverZone(currentTarget, clientX, clientY);
                      }, 25);
                  }
              }
            : null;

    const onFocus = () => {
        setFocused(true);
    };

    const onBlur = () => {
        setFocused(false);
    };

    const onPressIn = (event) => {
        pressedElementRef.current = event.currentTarget;
        handleGrant();
        setPressed(true);
    };

    const onPressOut = (event) => {
        handleRelease();
        setPressed(false);

        // On web, we want to lose the focus of this element after mouse press
        if (
            event.type === 'mouseup' &&
            pressedElementRef.current === document.activeElement &&
            // @ts-ignore
            typeof document.activeElement.blur === 'function'
        ) {
            // @ts-ignore
            document.activeElement.blur();
        }

        pressedElementRef.current = null;
    };

    // on unmount, clear the timeouts
    React.useEffect(() => {
        return () => {
            clearHoverTimeout();
            if (hoverZoneTimeoutRef.current) {
                clearTimeout(hoverZoneTimeoutRef.current);
            }
        };
    }, []);

    const domEvents: InteractiveState['domEvents'] = {
        // @ts-ignore
        onFocus: combineEventHandlers(onFocus, rest.onFocus),
        // @ts-ignore
        onBlur: combineEventHandlers(onBlur, rest.onBlur),
        onMouseEnter: combineEventHandlers(onMouseEnter, rest.onMouseEnter),
        onMouseLeave: combineEventHandlers(onMouseLeave, rest.onMouseLeave),
        // @ts-ignore
        onMouseMove: detectHoverZone
            ? // @ts-ignore
              combineEventHandlers(onMouseMove, rest.onMouseMove)
            : undefined,
        // @ts-ignore
        onMouseDown: combineEventHandlers(onPressIn, rest.onMouseDown),
        // @ts-ignore
        onMouseUp: combineEventHandlers(onPressIn, rest.onMouseUp),
    };

    return {
        dragging,
        focused,
        pressed,
        active: focused || (!dragging && hovered),
        hovered: !dragging && hovered,
        hoverZone,
        domEvents,
        events: {
            ...gestureHandlers,
            onFocus: domEvents.onFocus,
            onBlur: domEvents.onBlur,
            onMouseEnter: domEvents.onMouseEnter,
            onMouseLeave: domEvents.onMouseLeave,
            onMouseMove: domEvents.onMouseMove,
            // prevent hover showing while responder
            onResponderGrant: combineEventHandlers(handleGrant, rest.onResponderGrant),
            onResponderRelease: combineEventHandlers(handleRelease, rest.onResponderRelease),
            // if child is Touchable
            onPressIn: combineEventHandlers(onPressIn, rest.onPressIn),
            onPressOut: combineEventHandlers(onPressOut, rest.onPressOut),
        },
    };
}

/**
 * Utility component to wrap interactive components.
 */
export function WithInteractiveState<T extends React.ReactElement = React.ReactElement>(
    props: {
        children: (state: InteractiveState) => T;
    } & InteractiveOptions
): T {
    const { children, ...rest } = props;
    const state = useInteractiveState(rest);
    return children(state);
}

function combineEventHandlers<E>(
    a: (event: E) => void,
    b: undefined | ((event: E) => void)
): (event: E) => void {
    if (!b) {
        return a;
    }

    return (event: E) => {
        a(event);
        b(event);
    };
}
